/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * Envision customizations
 * assets/custom.js
 *
 * Custom JavaScript
 */

/**
 * Utility functions
 */

function waitForElm(selector, doc = document) {
  return new Promise(resolve => {
    if (doc.querySelector(selector)) {
      return resolve(doc.querySelector(selector));
    }

    const observer = new MutationObserver(mutations => {
      if (doc.querySelector(selector)) {
        resolve(doc.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(doc, {
      childList: true,
      subtree: true
    });
  });
}

function debounce(fn, delay = 500) {
  let timeout;
  return function (...args) {
    if (!timeout) {
      timeout = setTimeout(() => (timeout = null), delay);
      return fn(...args);
    }

    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      fn(...args);
    }, delay);
  };
}

/**
 * This code handles the collapsing and expanding of the search bar in the header
 * based on the 'searchCollapse' media query defined in theme.mediaMatches.
 * When the breakpoint is matched, the search bar is collapsed and a search button is shown.
 * When the breakpoint is not matched, the search bar is shown and the button is hidden (on first run).
 * The code listens for breakpoint changes and runs once on DOMContentLoaded.
 */

// After theme.mediaMatches is set up
function handleSearchCollapseBreakpoint() {
  // console.log('handleSearchCollapseBreakpoint');

  const header = document.querySelector('store-header');
  const headerSearch = header?.querySelector('.header__search');
  const searchButton = header?.querySelector('.js-show-search');

  // console.log(theme.mediaMatches.searchCollapse);

  if (theme.mediaMatches.searchCollapse) {
    // Collapse search bar, show button
    header?.classList.add('search-is-collapsed');
    header?.classList.remove('search-is-visible');
    headerSearch?.classList.add('header__search--collapsible-desktop');
    headerSearch?.classList.add('p-0', 'w-0');
    searchButton?.classList.remove('md:hidden');
  } else {
    // Show search bar, hide button (only first run)
    header?.classList.remove('search-is-collapsed');
    header?.classList.add('search-is-visible');
    headerSearch?.classList.remove('p-0', 'w-0');
    if (!handleSearchCollapseBreakpoint.hasRun) {
      searchButton?.classList.add('md:hidden');
      handleSearchCollapseBreakpoint.hasRun = true;
    }
  }
}

// Listen for breakpoint changes
window.addEventListener('on:breakpoint-change', handleSearchCollapseBreakpoint);
// Run once on load
document.addEventListener('DOMContentLoaded', handleSearchCollapseBreakpoint);

/**
 * Product Card Tooltips
 * =================================================================================================
 * Global tooltip functionality for mini product cards throughout the site.
 * Works with products that have been configured to show tooltips in theme settings.
 */

document.addEventListener('DOMContentLoaded', function() {
    // Initialize tooltip event handlers
    const tooltipSelectors = '.tooltip';
    const closeSelectors = '.tooltip__close';
    
    document.querySelectorAll(tooltipSelectors).forEach(element => {
        element.addEventListener('click', showTooltip);
        element.addEventListener('mouseover', showTooltip);
    });
    
    document.querySelectorAll(closeSelectors).forEach(element => {
        element.addEventListener('click', hideTooltip);
    });

    // Click outside to close tooltips
    document.addEventListener('click', function(event) {
        // Don't close if clicking on the tooltip icon OR the tooltip content
        if (!event.target.closest('.tooltip') && !event.target.closest('.tooltip-content')) {
            document.querySelectorAll('.tooltip-content').forEach(element => {
                element.classList.remove('show-tooltip');
            });
        }
    });
});

function showTooltip(event) {
  event.stopPropagation();
  const tooltipIcon = event.currentTarget;
  const tooltip = tooltipIcon.closest('.tooltip');
  const container = tooltip.parentElement;
  const tooltipContent = container.querySelector('.tooltip-content');

  // Determine the element to center on
  const centerOnSelector = tooltip?.dataset.centerOn;
  let centerElement = null;
  if (centerOnSelector) {
    centerElement = tooltip.closest(centerOnSelector) || document.querySelector(centerOnSelector);
  }
  if (!centerElement) centerElement = tooltip;

  // Use offsetParent-relative math for robust, generic positioning
  const offsetParent = tooltipContent.offsetParent || document.documentElement;
  const parentRect = offsetParent.getBoundingClientRect();
  const centerRect = centerElement.getBoundingClientRect();

  // Measure at final scale to avoid mis-centering from CSS transforms
  const prevTransition = tooltipContent.style.transition;
  const prevOpacity = tooltipContent.style.opacity;
  const prevVisibility = tooltipContent.style.visibility;
  tooltipContent.style.transition = 'none';
  tooltipContent.style.opacity = '0';
  tooltipContent.style.visibility = 'hidden';
  tooltipContent.classList.add('show-tooltip');

  // Reset left before measuring to avoid stale width/left
  tooltipContent.style.left = '0px';
  let tRect = tooltipContent.getBoundingClientRect();

  const targetCenterX = centerRect.left + (centerRect.width / 2);
  let newLeft = targetCenterX - parentRect.left - (tRect.width / 2);

  // Clamp inside viewport (or offset parent bounds if it matches viewport)
  const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
  // Clamp within the viewport, translated to offsetParent coordinates
  const minLeft = 8 - parentRect.left; // small padding from left edge of viewport
  const maxLeft = (viewportWidth - 8) - parentRect.left - tRect.width; // padding from right edge
  newLeft = Math.max(minLeft, Math.min(newLeft, maxLeft));
  tooltipContent.style.left = `${Math.round(newLeft)}px`;

  // Recalc rect after moving
  tRect = tooltipContent.getBoundingClientRect();

  // Position the triangle to point at the icon center within the tooltip
  const triangleInner = tooltipContent.querySelector('.tooltip-content-triangle-inner');
  const triangleOuter = tooltipContent.querySelector('.tooltip-content-triangle-outer');
  const iconRect = tooltipIcon.getBoundingClientRect();
  const iconCenterX = iconRect.left + (iconRect.width / 2);
  // Align triangle tip to icon center. Small visual tweak (-4px) to perfectly align
  let triangleLeft = iconCenterX - tRect.left - 4;
  const minTriangleLeft = 15;
  const maxTriangleLeft = tRect.width - 30;
  triangleLeft = Math.min(Math.max(triangleLeft, minTriangleLeft), maxTriangleLeft);
  triangleInner.style.left = `${Math.round(triangleLeft)}px`;
  triangleOuter.style.left = `${Math.round(triangleLeft - 15)}px`;

  // Reveal with original transitions; remove inline opacity/visibility so class controls them
  tooltipContent.style.transition = prevTransition;
  tooltipContent.style.opacity = prevOpacity;
  tooltipContent.style.visibility = prevVisibility;
}

function hideTooltip(event) {
    event.stopPropagation();
    const tooltipIcon = event.currentTarget;
    const tooltipContent = tooltipIcon.closest('.tooltip-content');
    // Clear any inline styles set during measure so CSS class can fully control state
    tooltipContent.style.transition = '';
    tooltipContent.style.opacity = '';
    tooltipContent.style.visibility = '';
    tooltipContent.classList.remove('show-tooltip');
}

// Make tooltip functions globally accessible for inline event handlers
window.showTooltip = showTooltip;
window.hideTooltip = hideTooltip;

/**
 * Cart Compatibility Checker Instantiation
 * =================================================================================================
 * Handles incompatible products in both cart drawer and main cart.
 */

// Initialize cart compatibility checker for both cart drawer and main cart
document.addEventListener('DOMContentLoaded', function() {
  // Only instantiate if the cart drawer exists
  if (document.querySelector('cart-drawer')) {
    new window.CartCompatibilityChecker({
      contextSelector: 'cart-drawer',
      checkoutButtonSelector: 'button[type="submit"][name="checkout"][form="cart-drawer-form"]'
    });
  }

  // Only instantiate if the main cart form exists
  if (document.querySelector('form#cart')) {
    new window.CartCompatibilityChecker({
      contextSelector: 'form#cart',
      checkoutButtonSelector: 'button[name="checkout"]'
    });
  }
});

/**
 * Promo Messages
 * =================================================================================================
 * Handles the rendering of promo messages in the header and cart drawer.
 */

document.addEventListener('DOMContentLoaded', function() {
  // --- Helpers ---
  function getCountry(callback) {
    // Try cart, then IP, then fallback
    getCart(function(cart) {
      if (cart && cart.shipping_address && cart.shipping_address.country_code) {
        callback(cart.shipping_address.country_code);
        return;
      }
      // Check sessionStorage for cached IP country
      var cached = null;
      try {
        cached = JSON.parse(sessionStorage.getItem('promo_country_cache'));
      } catch (e) {}
      if (cached && cached.value && cached.expires > Date.now()) {
        callback(cached.value);
        return;
      }
      // Fetch from ipapi.co
      fetch('https://ipapi.co/country/')
        .then(r => r.text())
        .then(code => {
          if (code && code.length === 2) {
            sessionStorage.setItem('promo_country_cache', JSON.stringify({ value: code, expires: Date.now() + 3600 * 1000 }));
            callback(code);
          } else {
            callback('US');
          }
        })
        .catch(() => callback('US'));
    });
  }

  function getCart(callback) {
    fetch('/cart.js')
      .then(res => res.json())
      .then(callback)
      .catch(() => callback(null));
  }

  // Hardcoded exclusions for free shipping eligibility
  const FREE_SHIPPING_EXCLUDE_PRODUCT_IDS = new Set(['15224559501683']);
  const FREE_SHIPPING_EXCLUDE_HANDLES = new Set(['donate-a-holiday-spice-set', 'giftcard']);

  function isEligibleForFreeShipping(item) {
    try {
      if (!item) return false;
      // Exclude Shopify gift card lines
      if (item.gift_card === true) return false;
      // Exclude specific product by ID or handle
      if (FREE_SHIPPING_EXCLUDE_PRODUCT_IDS.has(String(item.product_id))) return false;
      if (FREE_SHIPPING_EXCLUDE_HANDLES.has(String(item.handle || '').toLowerCase())) return false;
      return true;
    } catch {
      return false;
    }
  }

  function computeEligibleSubtotalCents(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      if (!isEligibleForFreeShipping(item)) return sum;
      // Prefer final_line_price (post-discount), fallback to line_price, else price * quantity
      const cents =
        (typeof item.final_line_price === 'number' && item.final_line_price) ||
        (typeof item.line_price === 'number' && item.line_price) ||
        (typeof item.price === 'number' && item.quantity ? item.price * item.quantity : 0);
      return sum + (cents || 0);
    }, 0);
  }

  function parseFreeShippingMinimum(minimums, country) {
    // minimums: string like "USD:49\nCAD:100"
    // country: 'US', 'CA', etc.
    if (!minimums) return null;
    const lines = minimums.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    let currency = 'USD';
    if (country === 'CA') currency = 'CAD';
    // Try to find a line for the country
    let min = null;
    for (const line of lines) {
      if (country === 'CA' && line.match(/^CAD:/i)) min = line.split(':')[1];
      if (country === 'US' && line.match(/^USD:/i)) min = line.split(':')[1];
    }
    // Fallback: first number in the string
    if (!min && lines.length) {
      const match = lines[0].match(/([0-9.]+)/);
      if (match) min = match[1];
    }
    return min ? parseFloat(min) : null;
  }

  function parseFlatRateShipping(flatRates, country) {
    // flatRates: string like "USD:10\nCAD:40"
    if (!flatRates) return null;
    const lines = flatRates.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    let currency = 'USD';
    if (country === 'CA') currency = 'CAD';
    let rate = null;
    for (const line of lines) {
      if (country === 'CA' && line.match(/^CAD:/i)) rate = line.split(':')[1];
      if (country === 'US' && line.match(/^USD:/i)) rate = line.split(':')[1];
    }
    if (!rate && lines.length) {
      const match = lines[0].match(/([0-9.]+)/);
      if (match) rate = match[1];
    }
    return rate ? parseFloat(rate) : null;
  }

  function formatMoney(amount) {
    // amount in cents or float
    if (typeof amount === 'string') amount = parseFloat(amount);
    if (isNaN(amount)) return '';
    // Show as $X (no decimals)
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function renderPromoMessage(type, country, amount, flatRate) {
    const messages = window.themeSettings && window.themeSettings.promoMessages;
    if (!messages) return '';
    let msg = '';
    if (country === 'US') {
      if (type === 'initial') msg = messages.us_initial;
      else if (type === 'progress') msg = messages.us_progress;
      else if (type === 'goal') msg = messages.us_goal;
    } else if (country === 'CA') {
      if (type === 'initial') msg = messages.ca_initial;
      else if (type === 'progress') msg = messages.ca_progress;
      else if (type === 'goal') msg = messages.ca_goal;
    } else {
      msg = messages.other;
    }
    if (msg && amount !== undefined && amount !== null) {
      msg = msg.replace(/\{\{\s*amount\s*\}\}/g, amount);
    }
    if (msg && flatRate !== undefined && flatRate !== null) {
      msg = msg.replace(/\{\{\s*flat_rate\s*\}\}/g, flatRate);
    }
    return msg;
  }

  function renderFreeShippingNotice(country, cart) {
    const container = document.getElementById('free-shipping-notice');
    if (!container) return;

    // Only show for US or CA
    if (country !== 'US' && country !== 'CA') {
      container.innerHTML = '';
      return;
    }

    const alignStart = container.getAttribute('data-align-start') === 'true';
    const barPosition = container.getAttribute('data-bar-position') || 'bottom';
    const minStr = window.themeSettings && window.themeSettings.freeShippingMinimum;
    const min = parseFreeShippingMinimum(minStr, country);
    const colorScheme = window.themeSettings && window.themeSettings.freeShippingColorScheme;
    if (!cart || !min || cart.items.length === 0) {
      container.innerHTML = '';
      return;
    }
    // Use eligible subtotal (exclude gift cards and specified products)
    const eligibleSubtotalCents = computeEligibleSubtotalCents(cart);
    const subtotal = eligibleSubtotalCents / 100;
    const progress = Math.min(100, Math.round((subtotal / min) * 100));
    let message = '';
    const translations = window.theme && window.theme.translations && window.theme.translations.cart && window.theme.translations.cart.general;
    if (subtotal >= min) {
      message = translations && translations.free_shipping ? translations.free_shipping : '🎉 Woohoo! You\'ve got free shipping!';
    } else {
      const deficitCeil = Math.ceil(min - subtotal);
      let deficitMsg = translations && translations.free_shipping_deficit_html ? translations.free_shipping_deficit_html : `You're <strong>{{ amount }}</strong> away from free shipping!`;
      message = deficitMsg.replace(/\{\{\s*amount\s*\}\}/g, `<strong>${formatMoney(deficitCeil)}</strong>`);
    }
    const colorClass = colorScheme && colorScheme !== 'none' ? `color-scheme color-scheme--${colorScheme}` : '';
    const alignClass = alignStart ? '' : 'mx-auto text-center';
    let barTop = '', barBottom = '';
    if (barPosition === 'top') {
      barTop = `<div class="free-shipping-notice__bar mb-2" style="--progress: ${progress}%"></div>`;
    } else {
      barBottom = `<div class="free-shipping-notice__bar mt-2" style="--progress: ${progress}%"></div>`;
    }
    container.innerHTML = `
      <div class="free-shipping-container ${colorClass}">
        <div class="block ${alignClass} text-theme-heading" data-minimum="${min * 100}">
          ${barTop}
          <div class="small-text burgundy--regular free-shipping-notice__heading mb-2${alignStart ? '' : ' text-center'}">${message}</div>
          ${barBottom}
        </div>
      </div>
    `;
  }

  let lastRenderedCountry = null;

  function updatePromoMessages() {
    getCountry(function(country) {
      getCart(function(cart) {
        const minStr = window.themeSettings && window.themeSettings.freeShippingMinimum;
        const flatStr = window.themeSettings && window.themeSettings.flatRateShippingPrice;
        const min = parseFreeShippingMinimum(minStr, country);
        const flat = parseFlatRateShipping(flatStr, country);
        let type = 'initial';
        let amount = '';
        let flatRate = '';
        if (!cart || !min) {
          type = 'initial';
          amount = '';
          flatRate = '';
        } else {
          const subtotal = cart.total_price / 100; // Shopify returns cents
          if (subtotal >= min) {
            type = 'goal';
            amount = '';
            flatRate = '';
          } else if (subtotal > 0) {
            type = 'progress';
            const deficitCeil = Math.ceil(min - subtotal);
            amount = formatMoney(deficitCeil);
            flatRate = '';
          } else {
            type = 'initial';
            amount = formatMoney(min);
            flatRate = country === 'CA' ? formatMoney(flat) : '';
          }
        }
        // Recompute subtotal using eligible lines for message selection/amounts
        if (cart && min) {
          const eligibleSubtotalCents = computeEligibleSubtotalCents(cart);
          const eligibleSubtotal = eligibleSubtotalCents / 100;
          if (eligibleSubtotal >= min) {
            type = 'goal';
            amount = '';
            flatRate = '';
          } else if (eligibleSubtotal > 0) {
            type = 'progress';
            const deficitCeil = Math.ceil(min - eligibleSubtotal);
            amount = formatMoney(deficitCeil);
            flatRate = '';
          } else {
            type = 'initial';
            amount = formatMoney(min);
            flatRate = country === 'CA' ? formatMoney(flat) : '';
          }
        }
        // Update both containers
        var promoBar = document.getElementById('global-promo-message');
        var promoCart = document.getElementById('global-promo-message-cart');
        const html = renderPromoMessage(type, country, amount, flatRate);
        const messages = window.themeSettings && window.themeSettings.promoMessages;
        let link = '';
        if (messages) {
          if (country === 'US') link = messages.us_link;
          else if (country === 'CA') link = messages.ca_link;
          else link = messages.other_link;
        }
        let promoHtml = html;
        if (promoBar && link) {
          promoHtml = `<a href="${link}" class="w-full announcement-bar-link" role="banner" aria-label="Announcement bar link">${html}</a>`;
        }
        if (promoBar && promoBar.innerHTML !== promoHtml) promoBar.innerHTML = promoHtml;
        document.querySelector("announcement-bar").classList.remove("loading");

        // Only show cart promo for US/CA, clear otherwise
        if (promoCart) {
          if (country === 'US' || country === 'CA') {
            if (promoCart.innerHTML !== html) promoCart.innerHTML = html;
          } else {
            if (promoCart.innerHTML !== '') promoCart.innerHTML = '';
          }
        }

        renderFreeShippingNotice(country, cart);
      });
    });
  }

  // Listen for cart changes
  function listenCartChanges() {
    document.addEventListener('on:cart:add', updatePromoMessages);
    document.addEventListener('on:line-item:change', updatePromoMessages);
  }

  // Initial render
  updatePromoMessages();
  listenCartChanges();
  window.updatePromoMessages = updatePromoMessages;
});

/**
 * Quick Add Dropdown
 * =================================================================================================
 * Handles the quick add dropdown for product cards.
 */

document.addEventListener('DOMContentLoaded', () => {
  document.body.addEventListener('click', (event) => {
    const toggle = event.target.closest('.js-quick-add-dropdown-toggle');
    if (toggle) {
      // Close all other open dropdowns first
      document.querySelectorAll('.quick-add-dropdown-menu').forEach((menu) => {
        // Only close if it's not the one being toggled
        if (menu !== toggle.nextElementSibling) {
          menu.classList.add('hidden');
        }
      });
      const dropdownMenu = toggle.nextElementSibling;
      if (dropdownMenu) {
        dropdownMenu.classList.toggle('hidden');
      }
    } else if (!event.target.closest('.quick-add-dropdown')) {
      document.querySelectorAll('.quick-add-dropdown-menu').forEach((menu) => {
        menu.classList.add('hidden');
      });
    }
  });
});

/**
 * Gift Notes
 * =================================================================================================
 * Handles the synchronization of the values of all Gift Notes.
 */

(function () {
  const giftSubscriptionMessage = document.querySelector("#GiftSubcriptionMessage");
  const cartGiftNote = document.querySelector("#cart-gift-note");
  const cartPageGiftNote = document.querySelector("#cart-gift-note-page");

  [
    giftSubscriptionMessage,
    cartGiftNote,
    cartPageGiftNote
  ].forEach(giftNote => {
    if (giftNote) {
      giftNote.addEventListener('input', function (e) {
        let val = this.value;
        if (giftSubscriptionMessage) giftSubscriptionMessage.value = val;
        if (cartGiftNote) cartGiftNote.value = val;
        if (cartPageGiftNote) cartPageGiftNote.value = val;
      });

      giftNote.addEventListener('change', function (e) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'Accept': 'application/json'
          },
          body: new URLSearchParams({
            note: this.value
          })
        })
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.json();
        })
        .then(() => {
          console.log("success");
        })
        .catch(() => {
          console.log("error");
        });
      });
    }
  })
})();

/**
 * Proxy triggers: when a .js-trigger-bis is clicked, click the matching Klaviyo handle button
 * =================================================================================================
 */

document.addEventListener('DOMContentLoaded', () => {
  try {
    document.body.addEventListener('click', (evt) => {
      const btn = evt.target.closest('.js-trigger-bis');
      if (!btn) return;
      const handle = btn.getAttribute('data-p-handle');
      if (!handle) return;
      const target = document.querySelector(`a[data-klaviyo-handle='${handle}']`) || document.querySelector('.klaviyo-bis-trigger');
      if (target) {
        evt.preventDefault();
        target.click();
      }
    });
  } catch (e) {}
});